import { useState, useEffect } from 'react';

export default function useSession(props) {

	const { timeoutCallback } = props ? props : { timeoutCallback: null };
	const getSession = () => {
		const sessionString = localStorage.getItem('constituo-imaging-session');
		const userSession = JSON.parse(sessionString);
		return userSession;
	};

	// eslint-disable-next-line no-undef
	const url = API_URL;
	const heartbeatInterval = 1000 * 60; // 1 minute heartbeart
	const apiOptions = {
		credentials: 'include',
		method: 'POST',
		headers: { 'Content-Type': 'application/json' }
	}

	const [session, setSession] = useState(getSession());
	const [performingHeartbeat, setPerformingHeartbeat] = useState(false);
	const [performingLogout, setPerformingLogout] = useState(false);
	const [messageInfo, setMessageInfo] = useState({});
	const sessionChanged = (e) => {
		if (e.key === 'constituo-logged-on' && e.newValue === 'true')
			window.location.reload();
		else if (e.key === 'constituo-logging-off' && e.newValue === 'true')
			window.location = "../constituo/";
		else if (e.key === 'constituo-session-refresh')
			window.location.reload();
	};

	const CP_TYPE = {
		WORKSPACE: 1,
		INSTANCE: 108,
		CATEGORY: 109,
		ROLE: 4,
		GROUP: 3,
		USER: 2,
		SATELLITE: 17,
		DOCUMENT_TYPE: 107,
		ATTRIBUTE: 119,
		ORGANIZATION: 200,
		MESSAGE: 6,

		WORKFLOW: 110,
		QUERY: 101,
		FILE_SOURCE: 102,
		KEY_FILE: 103,
		DATABASE: 104,
		FORMATTER: 105,
		SERVICE_GROUP: 106,
		DATALIST: 117,
		STAMP: 120,

		DASHBOARD: 112,

		CUSTOM_SCRIPT: 114,
		LOCATION_IMPORT: 115,
		EMAIL_IMPORT: 116,
		API_IMPORT: 118,
		
		ROUTINE: 108,

		properties: {
			1: { id: 1, name: "Workspaces", code: "workspaces", superType: "system" },
			108: { id: 108, name: "Instances", code: "instances", superType: "system" },
			109: { id: 109, name: "Categories", code: "categories", superType: "system" },
			4: { id: 4, name: "Roles", code: "roles", superType: "system" },
			3: { id: 3, name: "Groups", code: "groups", superType: "system" },
			2: { id: 2, name: "Users", code: "users", superType: "system" },
			17: { id: 17, name: "Satellites", code: "satellites", superType: "system" },
			107: { id: 107, name: "Content Types", code: "documenttypes", superType: "system" },
			119: { id: 119, name: "Attributes", code: "attributes", superType: "system" },
			200: { id: 200, name: "Organization", code: "organization", superType: "system" },
			6: { id: 6, name: "Message", code: "messages", superType: "" },

			110: { id: 110, name: "Workflows", code: "workflows", superType: "config" },
			101: { id: 101, name: "Queries", code: "queries", superType: "config" },
			102: { id: 102, name: "File Sources", code: "filesources", superType: "config" },
			103: { id: 103, name: "Key Files", code: "keyfiles", superType: "config" },
			104: { id: 104, name: "Databases", code: "databases", superType: "config" },
			105: { id: 105, name: "Formatters", code: "formatters", superType: "config" },
			106: { id: 106, name: "Service Groups", code: "servicegroups", superType: "config" },
			117: { id: 117, name: "Data Lists", code: "datalists", superType: "config" },
			120: { id: 120, name: "Stamps", code: "stamps", superType: "config" },

			112: { id: 112, name: "Dashboards", code: "dashboards", superType: "dashboard" },

			114: { id: 114, name: "Custom Scripts", code: "customscripts", superType: "tools" },
			115: { id: 115, name: "Location Imports", code: "locationimports", superType: "tools" },
			116: { id: 116, name: "Email Uploads", code: "emailimports", superType: "tools" },
			118: { id: 118, name: "Apis", code: "apiimports", superType: "tools" },
			
			108: { id: 108, name: "Routines", code: "routines", superType: "controlpanel"}
		}
	};

	const CP_ACTION = {
		CREATE: 1,
		READ: 2,
		UPDATE: 3,
		DEL: 4,
		USE: 5,
		EXPORT: 6,
		PROMOTE: 7,
		COMMIT: 8,
		properties: {
			1: { id: 1, name: "Create", code: "create" },
			2: { id: 2, name: "Read", code: "read" },
			3: { id: 3, name: "Edit", code: "update" },
			4: { id: 4, name: "Delete", code: "delete" },
			5: { id: 5, name: "Use/Execute", code: "use" },
			6: { id: 6, name: "Export", code: "export" },
			7: { id: 7, name: "Promote", code: "promote" },
			8: { id: 8, name: "Commit", code: "commit" }
		}
	};

	useEffect(() => {
		window.addEventListener('storage', sessionChanged);
		return () => window.removeEventListener('storage', sessionChanged);
	}, []);

	const saveSession = (userSession, refresh) => {
		localStorage.setItem('constituo-imaging-session', JSON.stringify(userSession));
		localStorage.setItem('constituo-logging-off', 'false');
		localStorage.setItem('constituo-logged-on', 'true');
		localStorage.setItem('constituo-session-timeout-logout', 'false');		
		setSession(userSession);
		if (refresh) {
			localStorage.setItem('constituo-session-refresh', new Date().getTime());
		}

		// Once the user session is set, start the last activity check
		if (userSession.user != null && timeoutCallback) {
			setInterval(() => {
				heartbeat();
			}, heartbeatInterval);
		}

		recordUserActivity();
		heartbeat();
		
		//Update all the last role/instance prefs to current of each
		setPreference("LAST_ROLE_ORGANIZATION", userSession.currentRole.org_id);
		setPreference("LAST_ROLE_FOR_ORG_" + userSession.currentRole.org_id, userSession.currentRole.id);
		if (userSession.instance) setPreference("LAST_INSTANCE_FOR_ROLE_" + userSession.currentRole.id, userSession.instance.id);
	};

	const recordUserActivity = () => {
		if (session != null && session.user != null) {
			let now = new Date();
			localStorage.setItem(session.user.id + "__LAST_ACTIVITY", now.getTime());
			localStorage.setItem(session.user.id + "__LAST_ACTIVITY_LONG", now);
		}
	};

	const heartbeat = () => {
		if (session != null && session.user != null) {
			const orgTimeout = getSession() ? getSession().userOrganization.preferences.timeout : 60;
			const timeout = 1000 * 60 * (orgTimeout ? parseInt(orgTimeout) : 60); // organization timeout
			const timeoutWarning = 1000 * 60 * 5; // 5 minute timeout warning

			var now = new Date().getTime();
			var lastActivity = localStorage.getItem(session.user.id + "__LAST_ACTIVITY");
			var delta = now - lastActivity;

			persistLastActivity(lastActivity);

			if (delta >= timeout) {
				//If we've exceeded the timeout since the last activity, log the user out
				if (performingLogout) return;
				
				localStorage.setItem('constituo-session-timeout-logout', 'true');
				localStorage.setItem('constituo-logging-off', 'true');
				
				persistPreferences(false);
				timeoutCallback(false);

			} else if (delta >= (timeout - timeoutWarning)) {
				//If we haven't hit our timeout yet but we're within the timeout warning range, display the warning
				if (timeoutCallback != null) timeoutCallback(true);

			} else {
				//If the delta since our last activity hasn't yet reached our timeout, call the server keep alive
				if (performingHeartbeat) return;
				setPerformingHeartbeat(true);
				fetch(url + '/api/users/keepAlive', apiOptions
				).then((response) => {
					if (response.ok) { return response.json(); }
					else { throw new Error(response.status); }
				}).then((msgInfo) => {
					setPerformingHeartbeat(false);
					if (msgInfo != null) {
						setMessageInfo(msgInfo);
					}
				}).catch((err) => {
					setPerformingHeartbeat(false);
					if (err.message === 'Failed to fetch' || err.message === '403') {
					    fetch(url + '/public/activeSessionExists', {
					        method: 'GET',
					        credentials: 'include'
					    }).then(response => response.json())
					      .then(active => {
					          if (!active) {
					              performLogout();
					          }
					      }).catch(error => {
					          console.error('Unable to verify session after heartbeat failure:', error);
					      });
					} else {
						console.error(err);
					}
				});
			}
		}
	};

	const persistLastActivity = (lastActivityMs) => {
		if (session != null && session.user != null) {
			fetch(url + '/api/users/recordActivity?time=' + lastActivityMs, apiOptions
			).then((response) => {
				if (response.ok) { return response; }
				else { throw new Error(response.status); }
			}).catch((err) => {
				if (err === 403 || err === 503) {
					performLogout();
				}
			});
		}
	};

	const performLogout = () => {
		setPerformingLogout(true);
		localStorage.setItem('constituo-session-timeout-logout', 'true');
		localStorage.setItem('constituo-logging-off', 'true');
		fetch(url + '/perform_logout', apiOptions
		).then((response) => {
			if (response.ok) { return response; }
			else { throw new Error(response.status); }
		}).then(() => {
			localStorage.setItem('constituo-logging-off', 'true');
			localStorage.setItem('constituo-logged-on', 'false');
			window.location = "../constituo/";
		}).catch(() => {
			localStorage.setItem('constituo-logging-off', 'true');
			localStorage.setItem('constituo-logged-on', 'false');
			window.location = "../constituo/";
		});
	};

	const clearPreferences = () => {
		let localStorageLength = localStorage.length;
		let localStorageKeys = [];
		for (let i = 0; i < localStorageLength; ++i) {
			localStorageKeys.push(localStorage.key(i));
		}

		for (let x = 0; x < localStorageLength; ++x) {
			let currentKey = localStorageKeys[x];
			let splitKey = currentKey.split("__");
			if (splitKey.length === 2) {
				// If user id matches the first half of the key, 
				// add the pref to list and remove it from local storage
				if (session.user.id === splitKey[0]) {
					localStorage.removeItem(currentKey);
				}
			}
		}

		persistPreferences(true);
	};

	const persistPreferences = (stay_logged_in, finishedFunction) => {
		let preferences = [];

		var localStorageLength = localStorage.length;
		let localStorageKeys = [];
		for (let i = 0; i < localStorageLength; ++i) {
			localStorageKeys.push(localStorage.key(i));
		}

		for (let x = 0; x < localStorageLength; ++x) {
			let currentKey = localStorageKeys[x];
			let splitKey = currentKey.split("__");
			if (splitKey.length === 2) {
				// If user id matches the first half of the key,
				// add the pref to list and remove it from local storage
				if (session.user.id === splitKey[0]) {
					if (!splitKey[1].endsWith("*SKIP")) {
						let pref = {};
						pref.user_id = splitKey[0];
						pref.key = splitKey[1];
						pref.value = localStorage.getItem(currentKey);
						preferences.push(pref);
					}

					// Clear key from local storage
					if (!stay_logged_in) {
						localStorage.removeItem(currentKey);
					}
				}
			}
		}

		let preferenceOptions = {
			credentials: 'include',
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(preferences)
		};

		fetch(url + '/api/users/preferences', preferenceOptions
		).then((response) => {
			if (response.ok) { return response; }
			else { throw new Error(response.status); }
		}).then(() => {
			if (!stay_logged_in) {
				performLogout();
			} else if (finishedFunction != null) {
				finishedFunction();
			}
		}).catch((err) => {
			console.error(err);
			if (!stay_logged_in) {
				performLogout();
			} else if (finishedFunction != null) {
				finishedFunction();
			}
		});
	};

	const isOrgAdmin = () => {
		let r = session.currentRole;
		return r.super && !r.workspace;
	};

	const isWorkspaceAdmin = () => {
		let r = session.currentRole;
		return r.super && !isOrgAdmin() && r.workspace && r.workspace.id;
	};

	const isConstituo = () => {
		let org = session.userOrganization;
		if (org != null)
			return org.constituo;
		else
			return false;
	};

	const isCurrentOrgConstituo = () => {
		let org = session.organization;
		if (org != null)
			return org.constituo;
		else
			return false;
	};
	
	const mfaVerified = () => {
		if(session.mfaVerified!=null)
			return session.mfaVerified;
		else
			return "disabled";
	};

	const hasWorkspace = () => {
		if (session.currentRole != null && session.currentRole !== undefined
			&& session.currentRole.workspace != null && session.currentRole.workspace !== undefined
			&& session.currentRole.workspace.id != null && session.currentRole.workspace.id !== undefined)
			return true;
		else
			return false;
	};

	const hasInstance = () => {
		if (session.instance != null && session.instance !== undefined
			&& session.instance.id != null && session.instance.id !== undefined)
			return true;
		else
			return false;
	}

	const getType = (code) => {
		for (let key in CP_TYPE.properties)
			if (CP_TYPE.properties[key].code === code)
				return CP_TYPE.properties[key];
		return null;
	};

	const getAction = (actionCode) => {
		return CP_ACTION.properties[CP_ACTION[actionCode]];
	};

	const hasAddPermission = (typeCode) => {
		if (typeCode == "dashboards") return true;
		else return hasEntityPermission(typeCode, getAction("CREATE"), null);
	};

	const hasViewPermission = (typeCode, id) => {
		return hasEntityPermission(typeCode, getAction("READ"), id);
	};

	const hasEditPermission = (typeCode, id, isPrivate) => {
		if (typeCode == "dashboards" && isPrivate) return true;
		else return hasEntityPermission(typeCode, getAction("UPDATE"), id);
	};

	const hasUsePermission = (typeCode, id) => {
		return hasEntityPermission(typeCode, getAction("USE"), id);
	};

	const hasDeletePermission = (typeCode, id) => {
		return hasEntityPermission(typeCode, getAction("DEL"), id);
	};

	const hasExportPermission = (typeCode, id) => {
		return hasEntityPermission(typeCode, getAction("EXPORT"), id);
	};

	const hasCommitPermission = (typeCode, id) => {
		return hasEntityPermission(typeCode, getAction("COMMIT"), id);
	};

	const hasEntityPermission = (typeCode, action, entityId) => {
		if (isOrgAdmin() || isWorkspaceAdmin()) return true;
		let type = getType(typeCode);
		var key = 'S7-' + type.id + '-' + action.id + '-';
		if (session.currentRole.allPerms[key + 'all']) return true;
		if (entityId && session.currentRole.allPerms[key + entityId]) return true;
		return false;
	};

	const hasAnyPermissions = (typeCode) => {
		let type = getType(typeCode);
		if (type == null) return false;
		let allPerms = session.currentRole.allPerms;
		for (let key in allPerms) {
			if (key.startsWith('S7-' + type.id + '-'))
				return true;
		}
		return false;
	}

	const hasAnySystemPermissions = () => {
		if (isOrgAdmin()) return true;
		let allPerms = session.currentRole.allPerms;
		for (let key in allPerms) {
			let perm = allPerms[key];
			if (perm === "system") return true;
		}
		return false;
	};

	const hasAnyConfigPermissions = () => {
		if (!hasInstance()) return false;
		if (isWorkspaceAdmin()) return true;
		let allPerms = session.currentRole.allPerms;
		for (let key in allPerms) {
			let perm = allPerms[key];
			if (perm === "config") return true;
		}
		return false;
	};

	const hasAnyToolsPermissions = () => {
		if (!hasInstance()) return false;
		if (isWorkspaceAdmin()) return true;
		let allPerms = session.currentRole.allPerms;
		for (let key in allPerms) {
			let perm = allPerms[key];
			if (perm === "tools") return true;
		}
		return false;
	}

	const getRouteParts = (route) => {
		let parts = route.split("/");
		return {
			type: parts[1],
			action: (parts.length > 2 && parts[2] != "" ? parts[2] : null),
			id: (parts.length > 3 ? parts[3] : null)
		};
	};

	const getCPAction = (action) => {
		if (action == 'add') return getAction('CREATE');
		else if (action == 'modify') return getAction('UPDATE');
		else if (action == 'view') return getAction('READ');
		else return null;
	};

	const hasMenuType = (navItem, visible = true) => {
		if(navItem == null) return false;
		if (navItem.requiresConstituo != null && navItem.requiresConstituo == true) return visible && isCurrentOrgConstituo() && isOrgAdmin();
		
		// feature driven items
		if (navItem.id === "emailimports" && !session.features['S7-AllowInboundEmail']) return false;

		// org admin
		if (isOrgAdmin()) {
			if (navItem.orgAdminAllowed) return visible && true;
			else return false;
		}

		// workspace admin
		if (isWorkspaceAdmin()) {
			if (!navItem.workspaceAdminAllowed) return false;
			if (navItem.requiresWorkspace && !hasWorkspace()) return false;
			if (navItem.requiresInstance && !hasInstance()) return false;
			return visible && true;
		}

		// end user
		if (navItem.id === "user" || navItem.id === "dashboards")
			return visible;
		if (navItem.id === "admin")
			return hasAnyConfigPermissions();
		if (navItem.id === "tools")
			return hasAnyToolsPermissions();
		if (navItem.id === "system")
			return hasAnySystemPermissions();
		if (navItem.id === "filtered")
			return hasAnyConfigPermissions() || hasAnyToolsPermissions() || hasAnySystemPermissions();
		if (navItem.parentId === "filtered")
			return visible;

		return visible && hasAnyPermissions(navItem.id);
	};

	const hasRoute = (route, navItems) => {
		let routeTypes = navItems.reduce((mapping, current) => {
			if (current.route) {
				const { type } = getRouteParts(current.route);
				mapping[type] = current;
			}

			return mapping;
		}, {});
		const { type, action, id } = getRouteParts(route);
		const navItem = routeTypes[type];

		let allowed = hasMenuType(navItem);
		if (action && type !== 'dashboards') allowed &= hasEntityPermission(type, getCPAction(action), id);
		if (("false")==mfaVerified()) return false;
		return allowed;
	}

	const getRoutableInboxes = (workflowId, inboxId) => {
		let workflow = getWorkflow(workflowId);
		if (workflow == null)
			return [];
		else {
			let inboxes = Object.keys(workflow.inboxes)
				.map((inboxId) => workflow.inboxes[inboxId])
				.filter((inbox) => inbox.permissions.routeTo)
				.filter((inbox) => inbox.id !== inboxId);
			inboxes.sort((a, b) => a.order - b.order || a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
			return inboxes;
		}
	};

	const hasEsign = () => {
		return session.features['S7-AllowEsign'] == "true";
	};
	
	const hasCreateEsign = (workflowId, inboxId) => {
		if (!hasEsign()) return false;
		else return hasWorkflowPermission(workflowId, inboxId, "createEsignDocument");
	};

	const hasScanPermission = (workflowId, inboxId, permission) => {
		if (!session.user.scan) return false;
		if (!session.features['S7-ScanUsers']) return false;
		if (session.features['S7-ScanUsers'] == "0") return false;
		else return hasWorkflowPermission(workflowId, inboxId, permission);
	};

	const hasWorkflowPermission = (workflowId, inboxId, permission) => {
		let workflow = getWorkflow(workflowId);
		if (workflow == null) return false;
		else return checkWorkflowForPermission(workflow, inboxId, permission);
	};

	const checkWorkflowForPermission = (workflow, inboxId, permission) => {
		if (inboxId != null) return workflow.inboxes[inboxId] && workflow.inboxes[inboxId].permissions[permission];
		else return true;
	};

	const getWorkflow = (workflowId) => {
		let workflows = session.currentRole.workflows[session.instance.id];
		for (let i = 0; i < workflows.length; i++)
			if (workflows[i].id === workflowId)
				return workflows[i];
		return null;
	};
	
	const getAddableStamps = () => {
		let stamps = session.currentRole.stamps[session.instance.id];
		if (stamps === undefined) return [];
		let addable = Object.keys(stamps).map((stampId) => {
			if (stamps[stampId].addStamp)
				return stamps[stampId].stamp;
			else
				return undefined;
		}).filter((elem) => elem !== undefined);
		addable.sort((a, b) => {
			let fa = a.name.toLowerCase(), fb = b.name.toLowerCase();
			if (fa < fb) return -1;
			if (fa > fb) return 1;
			return 0;
		});
		return addable;
	};

	const hasStampViewPermission = (stampId) => {
		return hasStampPermission(stampId, "viewStamp");
	};
	
	const hasStampAddPermission = (stampId) => {
		return hasStampPermission(stampId, "addStamp");
	};
	
	const hasStampModifyPermission = (stampId) => {
		return hasStampPermission(stampId, "modifyStamp");
	};

	const hasStampDeletePermission = (stampId) => {
		return hasStampPermission(stampId, "deleteStamp");
	};

	const hasStampPermission = (stampId, perm) => {
		let stamps = session.currentRole.stamps[session.instance.id];
		if (stamps === undefined) return false;
		let stamp = stamps[stampId];
		if (stamp === undefined) return false;
		else return stamp[perm];
	}; 
	
	const getRecentStamps = () => {
		let recentStampIds = JSON.parse(getPreference(session.instance.id + '_STAMPS'));
		if (!recentStampIds) recentStampIds = [];
		let stamps = session.currentRole.stamps[session.instance.id];
		if (stamps === undefined) return [];
		return recentStampIds.map((stampId) => {
			if (stamps[stampId] && stamps[stampId].addStamp)
				return stamps[stampId].stamp;
			else return undefined;
		}).filter((elem) => elem !== undefined);
	};
	
	const getViewableDocumentTypes = () => {
		let docTypes = session.currentRole.documentTypes[session.instance.id];
		let viewable = Object.keys(docTypes).map((docId) => {
			if (docTypes[docId].viewDocument)
				return docTypes[docId].documentType;
			else 
				return undefined;
		}).filter((elem) => elem !== undefined);
		viewable.sort((a, b) => {
			let fa = a.name.toLowerCase(), fb = b.name.toLowerCase();
			if (fa < fb) return -1;
			if (fa > fb) return 1;
			return 0;			
		});
		return viewable;
	};
	
	const getIndexableDocumentTypes = () => {
		let docTypes = session.currentRole.documentTypes[session.instance.id];
		let indexable = Object.keys(docTypes).map((docId) => {
			if (docTypes[docId].addDocument && docTypes[docId].documentType.status == 'ACTIVE')
				return docTypes[docId].documentType;
			else
				return undefined;
		}).filter((elem) => elem !== undefined);
		indexable.sort((a, b) => {
			let fa = a.name.toLowerCase(), fb = b.name.toLowerCase();
			if (fa < fb) return -1;
			if (fa > fb) return 1;
			return 0;
		});
		return indexable;
	};

	const getRecentIndexableDocumentTypes = () => {
		let recentDocTypeIds = JSON.parse(getPreference(session.instance.id + '_DOCUMENT_TYPES'));
		if (!recentDocTypeIds) recentDocTypeIds = [];
		let docTypes = session.currentRole.documentTypes[session.instance.id];
		return recentDocTypeIds.map((docId) => {
			if (docTypes[docId] && docTypes[docId].addDocument && docTypes[docId].documentType.status == 'ACTIVE')
				return docTypes[docId].documentType;
			else return undefined;
		}).filter((elem) => elem !== undefined);
	};

	const hasDocumentTypePermission = (docTypes, permission) => {
		let allowed = true;
		let permittedDocTypes = getInstanceDocumentTypePermissions();
		if (!permittedDocTypes) return false;
		for (let i = 0; i < docTypes.length; i++)
			allowed &= (permittedDocTypes[docTypes[i].id] && permittedDocTypes[docTypes[i].id][permission]);
		return allowed;
	};

	const getInstanceDocumentTypePermissions = () => {
		return session.currentRole.documentTypes[session.instance.id];
	};

	const getPreference = (key) => {
		let user = session.user;
		if (user != null && key != null) {
			return localStorage.getItem(user.id + "__" + key);
		}
	};

	const getSessionPreference = (key) => {
		return getPreference(key + "*SKIP");
	};

	const setPreference = (key, value, newUser) => {

		if (session === null) return;
		let user = session.user;
		if (newUser) user = newUser;

		if (user != null && key != null) {
			if (value != null)
				localStorage.setItem(user.id + "__" + key, value);
			else
				localStorage.removeItem(user.id + "__" + key);
		}
	};

	const setSessionPreference = (key, value) => {
		setPreference(key + "*SKIP", value);
	};

	const isAlreadyLoggedOn = () => {
		return localStorage.getItem("constituo-logged-on") === "true";
	};

	const setLoggedOn = () => {
		localStorage.setItem('constituo-logged-on', 'true');
	};
	
	const getUserLoginMessage = () => {
		if (session == null) return;
		if (session.userOrganization.preferences == null) return;
		if (session.userOrganization.loginMessage == null) return;
	
		// Must be enabled
		if (session.userOrganization.preferences['useLoginMessage'] !== 'true') return;
	
		// Must have a rate
		const rate = session.userOrganization.preferences['loginMessageRate'];
		if (rate == null) return;
		
		//Get login message viewed from session pref
		const loginMessageViewed = (getSessionPreference("LOGIN_MESSAGE_VIEWED") === "true");
	
		// ONCE: show only if user has never seen it (persisted on user object)
		if (rate === "ONCE") {
			const lastUpdatedMs = Date.parse(session.userOrganization.loginMessage.lastUpdatedDate);
			const lastReadMs = Date.parse(session.user.loginMessageLastRead);
			if (!isNaN(lastReadMs) && !isNaN(lastUpdatedMs) && lastReadMs >= lastUpdatedMs) return;
			
			return session.userOrganization.loginMessage.body;
		}
	
		// SESSION: always return message; UI will gate with loginMessageViewed
		if (rate === "SESSION"  && !loginMessageViewed) {
			return session.userOrganization.loginMessage.body;
		}
	
		// Unknown rate: don't show
		return;
	};
	
	const setLoginMessageViewed = () => {
		setSessionPreference("LOGIN_MESSAGE_VIEWED", "true");
		
		fetch(API_URL + "/api/session/loginMsgViewed?", {
			method: 'PUT',
			credentials: 'include'
		}).then(function(response) {
			if (!response.ok) {
				throw Error(response.status);
			} 
			return response.json();
		}).then(function(newSession) {
			setSession(newSession, true);
			window.location.reload();
		}).catch(function(err) {
			sessionChanged();
			let errMsg = (err.message === '403') ? ("Unable to switch roles.") : ("Failed to switch roles, please contact Support.");
			dispatch(addMessage({ type: 'error', msg: errMsg }));
		});
	};

	return {
    	setSession: saveSession,
    	getSession: getSession,
    	session: session,
    	recordUserActivity: recordUserActivity,
    	persistPreferences: persistPreferences,
    	clearPreferences: clearPreferences,
    	isOrgAdmin: isOrgAdmin,
    	isWorkspaceAdmin: isWorkspaceAdmin,
    	isConstituo: isConstituo,
    	hasWorkspace: hasWorkspace,
    	hasInstance: hasInstance,
    	hasEntityPermission: hasEntityPermission,
    	hasAddPermission: hasAddPermission,
    	hasViewPermission: hasViewPermission,
    	hasEditPermission: hasEditPermission,
    	hasUsePermission: hasUsePermission,
    	hasDeletePermission: hasDeletePermission,
    	hasExportPermission: hasExportPermission,
    	hasCommitPermission: hasCommitPermission,
    	hasAnySystemPermissions: hasAnySystemPermissions,
    	hasAnyConfigPermissions: hasAnyConfigPermissions,
    	hasAnyToolsPermissions: hasAnyToolsPermissions,
    	hasAnyPermissions: hasAnyPermissions,
    	hasRoute: hasRoute,
    	hasMenuType: hasMenuType,
    	hasScanPermission: hasScanPermission,
    	hasWorkflowPermission: hasWorkflowPermission,
		getAddableStamps: getAddableStamps,
		getRecentStamps: getRecentStamps,
		hasStampViewPermission: hasStampViewPermission,
		hasStampAddPermission: hasStampAddPermission,
		hasStampModifyPermission: hasStampModifyPermission,
		hasStampDeletePermission: hasStampDeletePermission,
    	getViewableDocumentTypes: getViewableDocumentTypes,
    	getIndexableDocumentTypes: getIndexableDocumentTypes,
    	getRecentIndexableDocumentTypes: getRecentIndexableDocumentTypes,
    	hasDocumentTypePermission: hasDocumentTypePermission,
    	getRoutableInboxes: getRoutableInboxes,
    	getPreference: getPreference,
    	getSessionPreference: getSessionPreference,
    	setPreference: setPreference,
    	setSessionPreference: setSessionPreference,
    	messageInfo: messageInfo,
    	setLoggedOn: setLoggedOn,
    	isAlreadyLoggedOn: isAlreadyLoggedOn,
    	getUserLoginMessage: getUserLoginMessage,
    	setLoginMessageViewed : setLoginMessageViewed,
		hasEsign : hasEsign,
		hasCreateEsign : hasCreateEsign
  	};
}